import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localePl from '@angular/common/locales/pl';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { AuthenticationModule } from '@authentication/authentication.module';
import { AuthService } from '@authentication/services/auth.service';
import { iifFreeOrCustomLicenseValid } from '@core/helpers/license/license.helper';
import { AcceptLanguageHeaderInterceptor } from '@core/interceptors/accept-language-header.interceptor';
import { AuthErrorInterceptor } from '@core/interceptors/auth-error.interceptor';
import { TokenInterceptor } from '@core/interceptors/token.interceptor';
import { UnknownErrorInterceptor } from '@core/interceptors/unknown-error.interceptor';
import { LicenseService } from '@core/services/license/license.service';
import { MatPaginatorTranslateService } from '@core/services/mat-paginator-translate.service';
import { environment } from '@env/environment';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { MyAccountModule } from '@shared/component/my-account/my-account.module';
import { proprietaryIcons } from '@shared/component/proprietary-icons/proprietary-icons';
import { DinamicaGeneraleService } from '@shared/dinamica-generale/services/dinamica-generale.service';
import { Language } from '@shared/model/languages.enum';
import { RoutePath } from '@shared/route-path.enum';
import { TableVirtualScrollModule } from 'ng-table-virtual-scroll';
import { TourMatMenuModule } from 'ngx-ui-tour-md-menu';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
registerLocaleData(localeDe, 'de');
registerLocaleData(localeEn, 'en');
registerLocaleData(localePl, 'pl');

@NgModule({
	declarations: [AppComponent],
	bootstrap: [AppComponent],
	imports: [
		AppRoutingModule,
		BrowserModule,
		BrowserAnimationsModule,
		AuthenticationModule,
		ReactiveFormsModule,
		FormsModule,
		MatSnackBarModule,
		TableVirtualScrollModule,
		TranslateModule.forRoot({
			defaultLanguage: Language.PL,
			useDefaultLang: true,
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient],
			},
		}),
		MyAccountModule,
		TourMatMenuModule,
	],
	providers: [
		{ provide: LOCALE_ID, useValue: navigator.language ?? 'pl' },
		{ provide: MAT_DATE_LOCALE, useValue: 'pl-PL' },
		provideMomentDateAdapter(),
		{
			provide: APP_INITIALIZER,
			useFactory: appInitializer,
			multi: true,
			deps: [AuthService, Router, DinamicaGeneraleService, LicenseService, MatIconRegistry, DomSanitizer],
		},
		{ provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: UnknownErrorInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: AuthErrorInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: AcceptLanguageHeaderInterceptor, multi: true },
		{
			provide: MatPaginatorIntl,
			useClass: MatPaginatorTranslateService,
		},
		provideHttpClient(withInterceptorsFromDi()),
	],
})
export class AppModule {}

class SfTranslateLoader implements TranslateLoader {
	constructor(private http: HttpClient) {}
	getTranslation(lang: string): Observable<any> {
		return this.http
			.get<object>(`../assets/i18n/${lang}-SF.json?th=${environment.th}`)
			.pipe(map((resp) => this.keyAsTranslation(resp)));
	}

	private keyAsTranslation(object): any {
		const newObject: any = {};
		for (const key in object) {
			if (Object.hasOwn(object, key)) {
				const keyTypeOf = typeof object[key];
				if (keyTypeOf === 'object') {
					newObject[key] = this.keyAsTranslation(object[key]);
				} else if (keyTypeOf === 'string') {
					newObject[key] = object[key] === '' ? `{{${key}}}` : object[key];
				}
			}
		}
		return newObject;
	}
}

export function HttpLoaderFactory(http: HttpClient) {
	return new SfTranslateLoader(http);
}

export function appInitializer(
	authService: AuthService,
	router: Router,
	dgService: DinamicaGeneraleService,
	licenseService: LicenseService,
	matIconRegistry: MatIconRegistry,
	domSanitizer: DomSanitizer
): () => Observable<any> {
	return () => {
		(() => {
			proprietaryIcons.forEach((icon) => {
				const fileName = icon.label;
				const safeUrl = domSanitizer.bypassSecurityTrustResourceUrl(`../assets/icons/${fileName}.svg`);
				const registrationLabel = `sf-${fileName}`;
				matIconRegistry.addSvgIcon(registrationLabel, safeUrl);
			});
		})();
		matIconRegistry.setDefaultFontSetClass('material-symbols-outlined');
		if (authService.tokenNotExpired) {
			return authService.refreshToken().pipe(
				catchError(() => {
					authService.clearAuthData();
					router.navigate([RoutePath.AUTH, RoutePath.LOGIN]);
					return EMPTY;
				}),
				switchMap(() => licenseService.license$),
				filter(
					(license) =>
						authService.user?.is_active && authService.hasUserRole() && iifFreeOrCustomLicenseValid(license, false)
				),
				switchMap(() => dgService.getLoginInfo()),
				tap((res) => dgService.setJwtData(res)),
				catchError(() => {
					return EMPTY;
				})
			);
		}
	};
}
